import {
    ActivityRecordType,
    activityRecordTypeLabels,
    CalendarDate,
    RegionId,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { ActivityRowsList } from "components/details-page-v-next/activity-rows";
import { DateRangeControl } from "components/reports/date-presets";
import { trpc } from "components/rpc/trpc";
import { useActivityTypeOptions } from "components/tasks/activity-type-options";
import { BlankSlate } from "components/ui/blank-slate";
import { FilterChipRow } from "components/ui/filter-chip-row";
import { ListSection } from "components/ui/list-section";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useNow, useRegionId } from "store/selectors/hooks";
import {
    activityDayLabel,
    availableActivityRecordTypes,
    filterActivityByRecordType,
    groupActivityByDate,
    MyActivityDay,
    MyActivityItem,
} from "./my-activity";
import { MyActivityEditingPanels, useMyActivityEditing } from "./my-activity-editing";
import { MyActivityItemRow, toMyActivityItem } from "./my-activity-item-row";
import {
    activityDatePresets,
    activityRangeIssue,
    defaultActivityRange,
    toActivityRangeInput,
} from "./my-activity-range";
import { useAgendaRefresh } from "./use-agenda-refresh";

export const MyActivityTab: React.FC<unknown> = () => {
    const now = useNow();
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const today = React.useMemo(() => CalendarDate.fromDate(now), [now]);
    const [range, setRange] = React.useState(() => defaultActivityRange(today));
    const presets = React.useMemo(() => activityDatePresets(today), [today]);
    const rangeIssue = activityRangeIssue(range);
    const activityTypes = useActivityTypeOptions();
    const refreshAgenda = useAgendaRefresh();
    const editing = useMyActivityEditing();

    const activityQuery = useQuery({
        ...trpcClient.touchpoint.myActivity.queryOptions({
            ...toActivityRangeInput(range),
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: !!regionId && !rangeIssue,
    });

    const [selectedRecordTypes, setSelectedRecordTypes] = React.useState<
        ActivityRecordType[]
    >([]);
    const toggleRecordType = (recordType: ActivityRecordType) =>
        setSelectedRecordTypes(selected =>
            selected.includes(recordType)
                ? selected.filter(existing => existing !== recordType)
                : [...selected, recordType],
        );

    const items: MyActivityItem[] = (activityQuery.data ?? []).map(toMyActivityItem);
    const recordTypeOptions = availableActivityRecordTypes(items).map(recordType => ({
        id: recordType,
        label: activityRecordTypeLabels[recordType],
    }));
    const filteredItems = filterActivityByRecordType(items, selectedRecordTypes);
    const days = groupActivityByDate(filteredItems);

    return (
        <div className="space-y-3 sm:space-y-4">
            <p className="text-sm text-gray-500">
                Everything you logged in the selected date range.
            </p>
            <DateRangeControl value={range} onChange={setRange} presets={presets} />
            {recordTypeOptions.length > 1 || selectedRecordTypes.length > 0 ? (
                <FilterChipRow
                    label="Filter:"
                    options={recordTypeOptions}
                    selectedIds={selectedRecordTypes}
                    onToggle={toggleRecordType}
                    onClear={() => setSelectedRecordTypes([])}
                    trailing={null}
                />
            ) : null}
            <MyActivityDays
                days={days}
                activityTypes={activityTypes}
                onSaved={refreshAgenda}
                editing={editing}
                rangeIssue={rangeIssue}
                isLoading={activityQuery.isLoading}
                hasItems={items.length > 0}
            />
            <MyActivityEditingPanels editing={editing} />
        </div>
    );
};

interface MyActivityDaysProps {
    days: MyActivityDay[];
    activityTypes: ReturnType<typeof useActivityTypeOptions>;
    onSaved: () => Promise<void>;
    editing: ReturnType<typeof useMyActivityEditing>;
    rangeIssue: string | null;
    isLoading: boolean;
    hasItems: boolean;
}

const MyActivityDays: React.FC<MyActivityDaysProps> = ({
    days,
    activityTypes,
    onSaved,
    editing,
    rangeIssue,
    isLoading,
    hasItems,
}) => {
    if (rangeIssue) {
        return <BlankSlate>{rangeIssue}</BlankSlate>;
    }

    if (isLoading) {
        return (
            <div className="flex justify-center overflow-hidden rounded-lg bg-white p-12 ring-1 ring-gray-300">
                <Spinner />
            </div>
        );
    }

    if (!hasItems) {
        return (
            <BlankSlate>
                No activity recorded in this date range. Emails, calls, texts, visits, and
                completed activities show up here.
            </BlankSlate>
        );
    }

    if (days.length === 0) {
        return <BlankSlate>No activity matches the selected types.</BlankSlate>;
    }

    return (
        <>
            {days.map(day => (
                <ListSection
                    key={day.date}
                    label={activityDayLabel(day.date)}
                    tone="accent"
                    count={day.items.length}
                    disclosure={{ kind: "static" }}>
                    <ActivityRowsList
                        className="px-2 sm:px-4"
                        testId={`my-activity-${day.date}`}>
                        {day.items.map(item => (
                            <li key={item.id}>
                                <MyActivityItemRow
                                    item={item}
                                    activityTypes={activityTypes}
                                    onSaved={onSaved}
                                    onEdit={editing.onEditFor(item)}
                                />
                            </li>
                        ))}
                    </ActivityRowsList>
                </ListSection>
            ))}
        </>
    );
};
